import { useEffect, useState } from 'react';

/**
 * Scroll-spy: returns the id of the section currently in the reading band near
 * the top of the viewport. Drives the docs sidebar highlight.
 *
 * The active band is "just under the sticky header down to ~45% of the
 * viewport" (rootMargin top = -topOffset, bottom = -55%). Whenever one or more
 * observed sections intersect that band, the first in document order wins — so
 * scrolling down promotes the next heading exactly as it reaches the top.
 *
 * `ids` must be a stable reference (module constant) so the observer isn't torn
 * down every render. Falls back to a no-op (returning the first id) in
 * environments without IntersectionObserver, e.g. jsdom.
 */
export function useScrollSpy(
  ids: readonly string[],
  topOffset = 96,
): string {
  const [activeId, setActiveId] = useState<string>(ids[0] ?? '');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const inBand = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id);
          else inBand.delete(entry.target.id);
        }
        // First section (document order) whose top has entered the band wins.
        const firstActive = ids.find((id) => inBand.has(id));
        if (firstActive) setActiveId(firstActive);
      },
      { rootMargin: `-${topOffset}px 0px -55% 0px`, threshold: 0 },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids, topOffset]);

  return activeId;
}
